import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, api, fmtDate, fmtINR } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, KeyRound, MapPin, Download, LogOut, Calendar } from "lucide-react";

export default function Portal() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState(() => localStorage.getItem("cgc_portal_token"));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      setStep("dashboard");
      loadBookings(token);
    }
  }, [token]);

  const loadBookings = async (t) => {
    setLoading(true);
    try {
      const r = await api.get("/portal/bookings", { headers: { Authorization: `Bearer ${t}` } });
      setBookings(r.data);
    } catch (err) {
      const code = err.response?.status;
      if (code === 401) { toast.error("Session expired — please sign in again"); logout(); }
      else if (code === 403) { toast.error("This link is not signed in as an attendee. Try signing in again."); logout(); }
      else { toast.error(err.response?.data?.detail || "Could not load your bookings"); }
    } finally { setLoading(false); }
  };

  const requestOtp = async (e) => {
    e.preventDefault();
    if (!email) return toast.error("Please enter your email");
    setLoading(true);
    try {
      const r = await api.post("/portal/request-otp", { email });
      setStep("otp");
      if (r.data?.dev_code) {
        toast.info(`Access code: ${r.data.dev_code}`, { duration: 15000 });
      } else {
        toast.success(`We've sent a 6-digit code to ${email}. Check your inbox.`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send code"); }
    finally { setLoading(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!code) return;
    setLoading(true);
    try {
      const r = await api.post("/portal/verify-otp", { email, code });
      localStorage.setItem("cgc_portal_token", r.data.token);
      setToken(r.data.token);
      toast.success("Signed in");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid code"); }
    finally { setLoading(false); }
  };

  const logout = () => {
    localStorage.removeItem("cgc_portal_token");
    setToken(null); setEmail(""); setCode(""); setBookings([]); setStep("email");
  };

  if (step === "dashboard") {
    const now = new Date().toISOString();
    const upcoming = bookings.filter((b) => (b.event?.start_at || "9999") >= now);
    const past = bookings.filter((b) => (b.event?.start_at || "9999") < now);
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="eyebrow">Your bookings</div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2">Hey {email}</h1>
            </div>
            <Button variant="outline" onClick={logout} data-testid="portal-logout"><LogOut size={14} className="mr-1.5" />Sign out</Button>
          </div>

          {loading && <div className="mt-10 text-stone-500">Loading your bookings…</div>}
          {!loading && bookings.length === 0 && <div className="mt-10 cgc-card p-8 text-center text-stone-500">No bookings yet under this email. <Link to="/events" className="underline text-[color:var(--brand-terracotta)]">Browse events</Link></div>}

          {upcoming.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-semibold mt-10 mb-3">Upcoming</h2>
              <div className="space-y-3">{upcoming.map((b) => <BookingCard key={b.id} b={b} />)}</div>
            </>
          )}
          {past.length > 0 && (
            <>
              <h2 className="font-display text-2xl font-semibold mt-10 mb-3">Past</h2>
              <div className="space-y-3 opacity-90">{past.map((b) => <BookingCard key={b.id} b={b} />)}</div>
            </>
          )}
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-md mx-auto px-4 py-16">
        <div className="eyebrow">Attendee portal</div>
        <h1 className="font-display text-3xl font-bold mt-2">See your bookings</h1>
        <p className="text-sm text-stone-500 mt-2">No account needed. We&apos;ll email you a one-time code.</p>

        {step === "email" && (
          <form onSubmit={requestOtp} className="mt-8 cgc-card p-6 space-y-4">
            <div>
              <Label className="flex items-center gap-1.5 text-xs"><Mail size={12} /> Your booking email</Label>
              <Input type="email" data-testid="portal-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-2" />
            </div>
            <Button type="submit" disabled={loading} className="w-full btn-brand h-11" data-testid="portal-request-otp">
              {loading ? "Sending…" : "Send me a code"}
            </Button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOtp} className="mt-8 cgc-card p-6 space-y-4">
            <div>
              <Label className="flex items-center gap-1.5 text-xs"><KeyRound size={12} /> 6-digit code</Label>
              <Input inputMode="numeric" pattern="[0-9]*" maxLength={6} data-testid="portal-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="mt-2 font-mono text-lg tracking-widest text-center" />
              <p className="text-[11px] text-stone-500 mt-2">The code is valid for 10 minutes. Check spam if you don&apos;t see it. Until SMTP is configured, staff will find the code in <code>backend.err.log</code>.</p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep("email")}>Back</Button>
              <Button type="submit" disabled={loading || code.length < 6} className="flex-1 btn-brand h-11" data-testid="portal-verify">
                {loading ? "Verifying…" : "Verify & continue"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </PublicLayout>
  );
}

function BookingCard({ b }) {
  const isConfirmed = b.status === "paid" || b.status === "free_confirmed";
  const status = isConfirmed ? "Confirmed" : b.status;
  return (
    <div className="cgc-card p-5 flex flex-col md:flex-row gap-4" data-testid={`portal-booking-${b.reference}`}>
      <img src={b.event?.cover_image} alt="" className="w-full md:w-40 h-32 md:h-24 object-cover rounded-md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className={`font-mono uppercase tracking-widest px-2 py-0.5 rounded text-[10px] font-semibold ${isConfirmed ? 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'}`}>{status}</span>
          <span className="font-mono text-stone-400">•</span>
          <span className="font-mono text-stone-500">{b.reference}</span>
        </div>
        <Link to={`/events/${b.event?.slug}`} className="font-display text-lg font-semibold mt-0.5 block hover:text-[color:var(--brand-terracotta)]">{b.event?.title || b.event_title}</Link>
        <div className="mt-1 text-xs text-stone-500 flex flex-wrap gap-3">
          {b.event?.start_at && <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(b.event.start_at)}</span>}
          {b.event?.venue_name && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {b.event.venue_name}</span>}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link to={`/booking/${b.reference}`}><Button size="sm" variant="outline">{isConfirmed ? "View tickets" : "View booking"}</Button></Link>
          {b.event?.google_maps_url && <a href={b.event.google_maps_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><MapPin size={12} className="mr-1" />Directions</Button></a>}
          {isConfirmed && b.attendees?.[0] && b.attendees[0].ticket_id && (
            <a href={`${API}/tickets/${b.attendees[0].ticket_id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download size={12} className="mr-1" />PDF</Button></a>
          )}
        </div>
      </div>
      <div className="text-right">
        <div className="text-xs text-stone-500">{isConfirmed ? "Total Paid" : "Total"}</div>
        <div className="font-display text-xl font-bold">{fmtINR(b.total_paise)}</div>
      </div>
    </div>
  );
}
